/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * The settings menu, shared by the settings pages and the rail.
 *
 * Two kinds of setting, kept apart: what is yours (your profile, what you are
 * told about) and how the organization is set up (who is here, which group
 * grants what, which providers are used). Both are reached from one Settings
 * entry in the account menu: a second entry that opened the same pages read as
 * one thing with two names. Kept here rather than in the settings layout so the rail can
 * use it without pulling a settings page into every other route's bundle.
 */
export const SETTINGS_GROUPS = [
  {
    heading: 'Your account',
    sections: [
      { to: '/settings/profile', label: 'Your profile', needs: undefined },
      { to: '/settings/notifications', label: 'Notifications', needs: undefined },
      { to: '/settings/out-of-office', label: 'Out of office', needs: undefined },
    ],
  },
  {
    heading: 'Organization',
    sections: [
      { to: '/settings/people', label: 'People', needs: 'admin:roles' },
      { to: '/settings/groups', label: 'Groups', needs: 'admin:roles' },
      {
        to: '/settings/counterparties',
        label: 'Merge counterparties',
        needs: 'admin:counterparties',
      },
      // Everybody reads Company details; only Admin, Leadership and Contracts
      // (`admin:organization`) change them or the letterhead (`ST-01`), and the
      // page says so to everybody else.
      { to: '/settings/company', label: 'Company details', needs: 'workflow:manage' },
      { to: '/settings/integrations', label: 'Integrations', needs: undefined },
      { to: '/settings/holidays', label: 'Holidays', needs: undefined },
      { to: '/settings/audit', label: 'Audit trail', needs: 'audit:read' },
    ],
  },
] as const;

/** Where Settings in the rail opens: the first organization page this person may see. */
export function organizationHome(
  permissions: Set<string> | undefined,
): '/settings/people' | '/settings/integrations' {
  return permissions?.has('admin:roles') ? '/settings/people' : '/settings/integrations';
}
